import React from "react";

import {
  mdiArchiveArrowDownOutline,
  mdiCog,
  mdiFilmstrip,
  mdiMicrophone,
  mdiSubtitlesOutline,
  mdiTelevisionBox,
  mdiVideoMarkerOutline,
} from "@mdi/js";
import Icon from "@mdi/react";
import { useTranslation } from "react-i18next";
import { useSelector } from "react-redux";

import FormatBadge from "./FormatBadge";
import { SectionTitle, SubSectionRow } from "./PanelSection";
import { AUDIO_FORMAT_LIST, VIDEO_FORMAT_LIST } from "../../constants/encoding";
import { OrganizationFastPlanOptions } from "../../types/organization.types";
import Block from "../block/Block";
import Button from "../button/Button";

import type { BadgeStatus } from "./FormatBadge";
import type { EncodingStream } from "../../api/EncoderAPI.types";
import type { FastAsset } from "../../api/MediaAPI.types";
import type { AppState } from "../../reducers";

interface OutputStatusPanelProps {
  streams: EncodingStream[];
  fastAsset: FastAsset | null | undefined;
  onViewDetails: () => void;
}

export function getAggregateStatus(
  groupStreams: EncodingStream[],
): BadgeStatus {
  if (
    groupStreams.length === 0 ||
    groupStreams.every((stream) => stream.status === null)
  ) {
    return "unavailable";
  }
  if (groupStreams.some((stream) => stream.status === "error")) return "error";
  if (groupStreams.every((stream) => stream.status === "transcoded"))
    return "transcoded";
  if (groupStreams.some((stream) => stream.status === "transcoding"))
    return "transcoding";
  return "pending";
}

export function getFastStatus(
  fastAsset: FastAsset | null | undefined,
  fastPlan: OrganizationFastPlanOptions,
): BadgeStatus {
  if (
    fastPlan === OrganizationFastPlanOptions.DISABLED ||
    fastPlan === OrganizationFastPlanOptions.FAST_LEGACY ||
    !fastAsset
  ) {
    return "unavailable";
  }

  const statusList =
    fastPlan === OrganizationFastPlanOptions.FAST
      ? [fastAsset.mp4_status]
      : [fastAsset.mp4_status, fastAsset.hls_status];

  if (statusList.some((status) => status === "error")) return "error";
  if (statusList.some((status) => status === "transcoding"))
    return "transcoding";
  if (statusList.every((status) => status === "transcoded"))
    return "transcoded";

  return "pending";
}

const SUBTITLES_VISIBLE_MAX = 2;

export interface GlobalStatusInput {
  video: BadgeStatus;
  audio: BadgeStatus;
  subtitles: BadgeStatus;
  chapters: BadgeStatus;
  d2g: BadgeStatus;
  fast: BadgeStatus;
}

export function getGlobalStatus(statusMap: GlobalStatusInput): BadgeStatus {
  const allStatusList = Object.values(statusMap);
  const activeStatusList = allStatusList.filter(
    (status) => status !== "unavailable" && status !== "neutral",
  );
  if (activeStatusList.length === 0) return "unavailable";
  if (activeStatusList.some((status) => status === "error")) return "error";
  if (activeStatusList.some((status) => status === "transcoding"))
    return "transcoding";
  if (activeStatusList.some((status) => status === "pending")) return "pending";

  const mainStatusList = [
    statusMap.video,
    statusMap.audio,
    statusMap.d2g,
    statusMap.fast,
  ];
  const hasMainEncoded = mainStatusList.some(
    (status) => status === "transcoded",
  );
  if (!hasMainEncoded) return "unavailable";

  return "transcoded";
}

export default function OutputStatusPanel({
  streams,
  fastAsset,
  onViewDetails,
}: OutputStatusPanelProps): React.JSX.Element {
  const { t } = useTranslation("editor-content");

  const activeStreams = streams.length > 0 ? streams : [];
  const activeFastAsset = fastAsset !== undefined ? fastAsset : null;

  const fastPlan = useSelector(
    (state: AppState) =>
      state.fetchReducer.orga_config.data?.organization_options.fast_plan,
  );

  const hasFastOption = useSelector(
    (state: AppState) =>
      state.fetchReducer.orga_config.data?.organization_options?.allow_fast,
  );

  const videoStreamList = activeStreams.filter((s) => s.type === "video");
  const audioStreamList = activeStreams.filter((s) => s.type === "audio");
  const subtitleStreamList = activeStreams.filter(
    (s) => s.type === "subtitles",
  );
  const chapterStreamList = activeStreams.filter((s) => s.type === "chapters");
  const d2gStreamList = activeStreams.filter((s) => s.type === "d2g");

  const visibleSubtitleList = subtitleStreamList.slice(
    0,
    SUBTITLES_VISIBLE_MAX,
  );
  const hiddenSubtitleCount = Math.max(
    0,
    subtitleStreamList.length - SUBTITLES_VISIBLE_MAX,
  );

  const visibleChapterList = chapterStreamList.slice(0, SUBTITLES_VISIBLE_MAX);
  const hiddenChapterCount = Math.max(
    0,
    chapterStreamList.length - SUBTITLES_VISIBLE_MAX,
  );

  const fastStatus = getFastStatus(activeFastAsset, fastPlan);

  const globalStatus = getGlobalStatus({
    video: getAggregateStatus(videoStreamList),
    audio: getAggregateStatus(audioStreamList),
    subtitles: getAggregateStatus(subtitleStreamList),
    chapters: getAggregateStatus(chapterStreamList),
    d2g: getAggregateStatus(d2gStreamList),
    fast: fastStatus,
  });

  const headerComponent = (
    <div className="flex flex-1 items-center justify-between gap-3">
      <FormatBadge
        withoutTooltip
        label={t(`streamStatus.${globalStatus}`)}
        status={globalStatus}
      />
      <Button
        onClick={onViewDetails}
        size="xs"
        type="button"
        variant="secondary"
      >
        <Icon
          className="text-muted-foreground"
          path={mdiArchiveArrowDownOutline}
          rotate={90}
          size={0.7}
        />
        {t("viewDetails")}
      </Button>
    </div>
  );

  return (
    <Block
      isFullSize
      bodyStyle="h-full"
      classNameHeader="h-[4.375rem]"
      headerComponent={headerComponent}
      title={t("outputStatusPanelTitle")}
    >
      <div className="flex flex-col gap-12">
        <div>
          <SectionTitle label={t("vodDelivery")} />
          <div className="flex flex-col gap-[0.78125rem]">
            <SubSectionRow icon={mdiFilmstrip} label={t("video")}>
              {VIDEO_FORMAT_LIST.map((resolution) => {
                const resolutionStreams = videoStreamList.filter(
                  (stream) =>
                    stream.manifest.quality === resolution ||
                    stream.manifest.quality.includes(
                      resolution.replace("p", ""),
                    ),
                );

                const videoStatus = getAggregateStatus(resolutionStreams);

                return (
                  <FormatBadge
                    key={resolution}
                    expand
                    label={resolution}
                    status={videoStatus}
                  />
                );
              })}
            </SubSectionRow>

            <SubSectionRow icon={mdiMicrophone} label={t("audio")}>
              {AUDIO_FORMAT_LIST.map((formatLabel) => {
                const bitrate = formatLabel.replace("kbps", "");
                const bitrateStreams = audioStreamList.filter((stream) =>
                  stream.manifest.quality?.includes(bitrate),
                );

                return (
                  <FormatBadge
                    key={formatLabel}
                    expand
                    count={bitrateStreams.length}
                    label={formatLabel}
                    status={getAggregateStatus(bitrateStreams)}
                  />
                );
              })}
            </SubSectionRow>

            {subtitleStreamList.length > 0 && (
              <SubSectionRow icon={mdiSubtitlesOutline} label={t("subtitles")}>
                {visibleSubtitleList.map((subtitle) => (
                  <FormatBadge
                    key={subtitle.uuid}
                    expand
                    label={subtitle.name || t("subtitles")}
                    status={getAggregateStatus([subtitle])}
                  />
                ))}
                {hiddenSubtitleCount > 0 && (
                  <FormatBadge
                    withoutIcon
                    withoutTooltip
                    label={t("plusXMore", { count: hiddenSubtitleCount })}
                    onClick={onViewDetails}
                    status={getAggregateStatus(
                      subtitleStreamList.slice(SUBTITLES_VISIBLE_MAX),
                    )}
                  />
                )}
              </SubSectionRow>
            )}

            {chapterStreamList.length > 0 && (
              <SubSectionRow icon={mdiVideoMarkerOutline} label={t("chapters")}>
                {visibleChapterList.map((chapter) => (
                  <FormatBadge
                    key={chapter.uuid}
                    expand
                    label={chapter.name || t("chapters")}
                    status={getAggregateStatus([chapter])}
                  />
                ))}
                {hiddenChapterCount > 0 && (
                  <FormatBadge
                    withoutIcon
                    withoutTooltip
                    label={t("plusXMore", { count: hiddenChapterCount })}
                    onClick={onViewDetails}
                    status={getAggregateStatus(
                      chapterStreamList.slice(SUBTITLES_VISIBLE_MAX),
                    )}
                  />
                )}
              </SubSectionRow>
            )}

            <SubSectionRow icon={mdiCog} label={t("offline")}>
              <FormatBadge
                expand
                label={t("d2g")}
                status={getAggregateStatus(d2gStreamList)}
              />
            </SubSectionRow>
          </div>
        </div>
        {hasFastOption ? (
          <div>
            <SectionTitle label={t("linearDelivery")} />
            <div className="flex flex-col gap-[0.78125rem]">
              <SubSectionRow icon={mdiTelevisionBox} label={t("fast")}>
                <FormatBadge
                  expand
                  label={`${t("fastAsset")} ${fastAsset?.framerate ? `${fastAsset.framerate} fps` : ""}`}
                  status={fastStatus}
                />
              </SubSectionRow>
            </div>
          </div>
        ) : null}
      </div>
    </Block>
  );
}
